import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, Info, Lock } from 'lucide-react';
import api from '@/api/axios';

/**
 * AI 보고서 사용 조건을 읽어 온다.
 *
 * 값의 주인은 플랫폼이다 — 어느 엔진을 쓸지는 기업이 Hub 에서 고르고,
 * 1건 얼마인지는 요금제가 정하고, 몇 자까지 보낼 수 있는지는 공급사가
 * 모델에 맞춰 정한다. 화면은 받아서 보여 주기만 한다.
 *
 * 두 화면(영업활동 보고서 · 프로젝트 보고서)이 같은 것을 써야 해서
 * 훅으로 뺐다. 한쪽만 고쳐 표시가 달라지는 일을 막는다.
 */
export function useAiConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  /*
   * 다시 읽기.
   *
   * 보고서를 하나 만들면 이번 달 사용 건수가 늘어난다. 다시 읽지 않으면
   * 「무료 5건 중 2건 사용」이 그대로 남아, 다음에 누를 때 **돈이 나가는지
   * 아닌지를 틀리게** 보여 준다.
   */
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/ai/config');
        if (alive) setConfig(data);
      } catch {
        // 못 읽으면 막아 둔다. 열어 두고 누르게 했다가 실패하는 것보다 낫다.
        if (alive) {
          setConfig({
            enabled: false,
            providerName: 'AI',
            disabledReason: 'AI 설정을 읽지 못했습니다. 잠시 뒤 다시 시도해 주세요.',
          });
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [tick]);

  return { config, loading, refresh };
}

/**
 * 이번 달 무료 몇 건 중 몇 건을 썼고, 넘기면 1건에 얼마인가.
 *
 * 「1건 300원」만 적어 두면, 무료분이 남아 있는데도 돈이 나가는 줄 알고
 * 안 누른다. 반대로 무료분을 다 쓴 사람은 돈이 나가는 줄 모르고 누른다.
 * **지금 누르면 돈이 나가는가** 가 한눈에 보여야 한다.
 *
 * 무료 수량은 **기업 단위**로 매월 1일에 새로 시작하고, 남은 것은 다음
 * 달로 넘어가지 않는다. 세는 쪽은 WORK&Hub 다 — 제품이 따로 세면 청구와
 * 어긋난다.
 */
/** `2026-10-01` → `2026년 10월`. 과금 기간의 첫날을 사람이 읽는 달 이름으로. */
function monthLabel(periodStart) {
  const s = periodStart ?? '';
  return s ? `${s.slice(0, 4)}년 ${Number(s.slice(5, 7))}월` : '이번 달';
}

export function AiQuotaLine({ config, loading }) {
  if (loading || !config || !config.enabled) return null;

  const month = monthLabel(config.periodStart);

  const tone = (config.freeRemaining ?? 0) > 0
    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
    : 'border-amber-200 bg-amber-50 text-amber-900';

  return (
    <div className={`rounded-md border px-3.5 py-2 text-sm ${tone}`}>
      {month}: 보고서 생성 <b>{config.usedThisMonth ?? 0}건</b>
      <span className="opacity-70">(무료 제공 {config.freePerMonth ?? 0}건/월)</span>
    </div>
  );
}

/**
 * 화면 설명 안에 들어가는 <b>살아 있는</b> 사용량 한 줄.
 *
 * ## 왜 숫자를 적어 두지 않나
 * 설명에 「무료 제공 5건/월」처럼 숫자를 박아 두면, 요금제가 바뀌는 순간
 * <b>설명만 거짓말이 된다.</b> 실제로 그랬다 — 무료 수량을 2건으로 내린 뒤에도
 * 네 화면의 설명이 5건이라고 말하고 있었다. 돈에 관한 숫자라 더 나쁘다.
 *
 * 그래서 보여 줄 숫자를 <b>요금제에서 그대로 읽어</b> 온다. 위 안내줄
 * ({@link AiQuotaLine})과 같은 값, 같은 서식이므로 설명과 화면이 어긋날 수 없다.
 *
 * 값을 못 읽거나 AI 를 못 쓰는 상태면 숫자 대신 자리만 알려 준다 — 설명을
 * 열었다는 이유로 틀린 수량을 보여 주지는 않는다.
 */
export function AiQuotaExample() {
  const { config, loading } = useAiConfig();

  if (loading) return <b>「…」</b>;
  if (!config?.enabled) return <b>「이번 달: 보고서 생성 ○건(무료 제공 ○건/월)」</b>;

  return (
    <b>
      「{monthLabel(config.periodStart)}: 보고서 생성 {config.usedThisMonth ?? 0}건
      (무료 제공 {config.freePerMonth ?? 0}건/월)」
    </b>
  );
}

/**
 * 보고서를 만들기 전에 받는 동의.
 *
 * 보고서를 만들면 고객사 정보와 영업·프로젝트 내용이 **외부 AI 모델로
 * 나간다.** 그 사실을 누르기 전에 알리고, 동의한 사실을 보고서마다
 * 남긴다(서버가 consented · consent_text 에 적는다).
 *
 * 문구는 서버가 준다 — 세 제품(Hub·ECO·CRM)이 같은 문장을 띄워야 하고,
 * 화면마다 적어 두면 갈라진다.
 */
export function AiConsentCheckbox({ config, checked, onChange, disabled }) {
  if (!config || !config.enabled) return null;

  const text =
    config.consentText ||
    '보고서 생성에 필요한 정보를 선택된 AI 모델에 제공하여 결과를 받는 것에 동의합니다.';

  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3.5 py-2.5">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm leading-relaxed text-gray-800">
        {text}
        <span className="mt-0.5 block text-xs text-gray-500">
          선택된 모델: <b>{config.providerName}</b> · 동의 여부는 보고서에 함께 기록됩니다.
        </span>
      </span>
    </label>
  );
}

/**
 * 제목 아래 한 줄 — <b>이 보고서는 WORK&amp;Hub 가 제공하는 AI 로 만든다.</b>
 *
 * <para>
 * 전에는 「기업이 선택한 AI 엔진은 <b>Claude</b> (claude-haiku-5-5) 이며…」
 * 처럼 <b>모델 이름과 판본</b>을 적었다. 쓰는 사람에게 그 이름은 고를 수도
 * 없고 바꿀 수도 없는 정보다 — 엔진은 공급사가 정한다. 오히려 "우리가 산
 * 것은 Claude 인가" 라는 오해를 만든다. 고객이 계약한 것은
 * <b>WORK&amp;Hub 의 AI 서비스</b>이고, 그 안에서 어느 모델을 돌리는지는
 * 공급사의 몫이다.
 * </para>
 * <para>
 * 모델 이름이 완전히 사라지지는 않는다 — <b>동의 칸</b>에는 그대로 적는다
 * ({@link AiConsentCheckbox}). 자료가 어디로 나가는지에 대한 동의라서,
 * 받는 쪽을 가리면 동의의 뜻이 흐려진다.
 * </para>
 * <para>
 * 두 화면(영업활동 보고서·프로젝트 보고서)이 같은 문장을 써야 해서 여기
 * 둔다. 한쪽만 고쳐 다른 말이 되는 일을 막는다.
 * </para>
 */
export function AiEngineLine({ config, loading }) {
  if (loading || !config) return null;

  return (
    <span>
      {' '}
      <b className="text-blue-600">WORK&amp;Hub</b> 에서 제공하는 AI 서비스를 이용합니다.
    </span>
  );
}

/**
 * 보고서 생성 단추 옆에 붙는 안내.
 *
 * 쓸 수 있으면 「WORK&amp;Hub AI · 초과 시 350원/건 · 최대 12만 자」,
 * 못 쓰면 왜 못 쓰는지.
 *
 * 비용을 **누르기 전에** 보여 주는 것이 핵심이다. 만들고 나서 청구서에서
 * 처음 보면 "왜 돈이 나갔지" 가 된다.
 *
 * 모델 이름은 적지 않는다 — 쓰는 사람이 고를 수 없는 값이고, 계약한 것은
 * WORK&amp;Hub 의 AI 서비스다({@link AiEngineLine} 참고).
 */
export default function AiReportNotice({ config, loading }) {
  if (loading) return null;
  if (!config) return null;

  if (!config.enabled) {
    return (
      <div className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 p-3.5">
        <Lock size={18} className="mt-0.5 shrink-0 text-amber-600" />
        <p className="text-sm leading-relaxed text-amber-900">
          <span className="font-black">AI 보고서를 쓸 수 없습니다.</span>{' '}
          {config.disabledReason}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm text-blue-900">
      <span className="flex items-center gap-1.5 font-black">
        <Sparkles size={15} /> WORK&amp;Hub AI
      </span>
      <span>
        <b>
          무료 수량 초과 시 건당 비용이 발생합니다.
          {config.pricePerReport != null &&
            `(${config.pricePerReport.toLocaleString('ko-KR')}원/건)`}
        </b>
      </span>
      <span className="text-blue-700">
        한 번에 최대 {(config.maxInputChars ?? 0).toLocaleString('ko-KR')}자
      </span>
      {/*
        돈 이야기는 위 한 줄로 끝낸다.
        「사용한 만큼 청구서에 더해집니다」까지 적으면, 5건은 무료인데도
        누를 때마다 돈이 나가는 것처럼 읽힌다.
      */}
      <span className="flex items-center gap-1 text-xs text-blue-700">
        <Info size={13} />
        만들다 실패하면 청구하지 않습니다.
      </span>
    </div>
  );
}
